import React, { useState, useEffect } from "react";

import { Container, Dropdown, Form, Grid, List } from "semantic-ui-react";
import InfiniteScroll from "components/ui/InfiniteScroll";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdoptionAccounts,
  fetchChurnedAccounts,
  fetchNewAccounts,
  fetchTrialAccounts,
  fetchSuspendedAccounts,
  fetchTerminatedAccounts,
  fetchTotalAccounts,
  fetchTotalAccountsTimeline,
  fetchTotalUsers,
  fetchTotalAssets,
  fetchTotalCollections,
  fetchTotalStreams,
  fetchTotalLeads,
  fetchDailyActiveUsers,
  fetchDailyActiveUsersTimeline,
  fetchWeeklyActiveUsers,
  fetchWeeklyActiveUsersTimeline,
  fetchMonthlyActiveUsers,
  fetchMonthlyActiveUsersTimeline,
  fetchIntegrations,
  fetchStreamCreation,
  fetchAssetCreation,
  fetchCollectionCreation,
  fetchAssetViewing,
  fetchAssetSharing,
  fetchEmailOpenRate,
  fetchLinkOpenRate,
  fetchAverageTimeSpent,
  fetchPersonalisedLinks,
  fetchAnonymousLinks,
  fetchSessions,
  fetchPersonalisedSessions,
  fetchAnonymousSessions,
  fetchUniqueVisitors,
  fetchSessionsCompletion,
} from "actions/adoptionMetrics";

import _, { filter } from "lodash";
import styles from "./dashboard.module.scss";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Link } from "react-router-dom";
import cx from "classnames";
import Select from "react-select";

import WeeklyAdoption from "./adoption/weekly";

import initIndustryOptions from 'data/industries.json';
import initCountryOptions from 'data/countries.json';
import initUseCaseOptions from 'data/useCases.json'

const intMrrOptions = [
  { key: 1, label: "0-50", value: "0-50" },
  { key: 2, label: "50-250", value: "50-250" },
  { key: 3, label: "250-500", value: "250-500" },
  { key: 4, label: "500-1000", value: "500-1000" },
  { key: 5, label: "1000-2000", value: "1000-2000" },
  { key: 6, label: "More than 2000", value: "More than 2000" },
];

const Accounts = ({ totalAccounts }) => {
  const accountData = [];

  const totalAccountsData = (totalAccounts && totalAccounts.data) || [];
  totalAccountsData.forEach((item) => {
    accountData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  return (
    <div className={styles.totalAccountsMetricsCol}>
      <h3 className={styles.title}># of Accounts</h3>

      <TimeLineChart
        count={totalAccounts.count}
        timeline={accountData}
        margin={{ top: 20, right: 10, bottom: 20, left: -25 }}
        tooltip="Paid Accounts"
      />
    </div>
  );
};

const AccountsList = ({ title, data }) => {
  const lastTwoWeek = moment()
    .subtract(14, "days")
    .format("DD-MMM-YYYY");

  return (
    <div className={styles.productMetricsCol}>
      <span className={styles.title}>
        {title} {data && <span>({data.length})</span>}{" "}
      </span>
      <div className={styles.since}> Since {lastTwoWeek} </div>
      {data.length > 0 && (
        <List className={styles.accountList}>
          {data.map((item, index) => {
            return (
              <List.Item>
                {index + 1}
                {"."}
                <Link
                  className={styles.accountInfo}
                  to={`/account/${item.id}/management`}
                  target="_blank"
                >
                  {item.name}
                </Link>
              </List.Item>
            );
          })}
        </List>
      )}

      {data.length === 0 && (
        <div className={styles.emptyMessage}>No Data found</div>
      )}
    </div>
  );
};

const TrialAccounts = ({ title }) => {
  const dispatch = useDispatch();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [today, setRemainingDays] = useState(new Date());
  const limit = 20;
  const data = useSelector((state) => state.adoptionMetrics.trialAccounts);

  const loading = data?.loading;
  const items = data?.items || [];
  const pagination = data?.pagination || {};
  const { currentPage = 0, totalPages = 0, totalRecordsCount = 0 } = pagination;

  const hasMorePages = items.length < totalRecordsCount;

  useEffect(() => {
    const timer = setInterval(() => setRemainingDays(new Date()), 24 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const getDaysLeft = (item) => {
    const referenceDate = item.changedDate || item.createdDate;
    const trialDays = item.noOfDays;

    if (!referenceDate || !trialDays) {
      return null;
    }

    const changed = new Date(referenceDate);
    const diffTime = today - changed;
    const remainingDays = Math.ceil(
      (trialDays * 24 * 60 * 60 * 1000 - diffTime) / (1000 * 60 * 60 * 24)
    );

    return remainingDays > 0 ? remainingDays : 0;
  };
  const sortedItems = [...items].sort((first, second) => {
  const daysFirst = getDaysLeft(first);
  const daysSecond = getDaysLeft(second);

  if (daysFirst === null && daysSecond === null) return 0;
  if (daysFirst === null) return 1;
  if (daysSecond === null) return -1;
  return daysFirst - daysSecond;
});
  useEffect(() => {
    dispatch(fetchTrialAccounts({ page: 0, limit }));
  }, []);

  const handleLoadMore = async () => {
    if (isFetchingMore || loading || items.length >= totalRecordsCount) {
      return;
    }

    setIsFetchingMore(true);
    try {
      await dispatch(fetchTrialAccounts({ page: currentPage + 1, limit }));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const showLoader = loading || isFetchingMore;

  return (
    <div className={styles.productMetricsCol}>
      <span className={styles.title}>
        {title} {totalRecordsCount > 0 && <span>({totalRecordsCount})</span>}
      </span>

      <Container>
        <InfiniteScroll
          loading={showLoader}
          currentPage={currentPage}
          totalPages={totalPages}
          pagination={true}
          setPage={handleLoadMore}
          hasMore={hasMorePages}
        >
          {sortedItems.length > 0 ? (
            <List className={styles.accountList}>
              {sortedItems.map((item, index) => {
                const daysLeft = getDaysLeft(item);

                return (
                  <List.Item key={item.id} className={styles.listItem}>
                    <span className={styles.index}>{index + 1}.</span>
                    <Link
                      to={`/paperflite/accounts/${item.id}/management`}
                      className={styles.accountLink}
                    >
                      {item.name}
                    </Link>
                    <span className={styles.daysLeftPill}>
                      {daysLeft !== null
                        ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                        : 'N/A'}
                    </span>
                    {index === 0 && (
                      <span className={styles.deleteIndicator}>Suspending soon...</span>
                    )}
                  </List.Item>
                );
              })}
            </List>
          ) : (
            !loading && <div className={styles.emptyMessage}>No Data found</div>
          )}
          {showLoader && (
            <div className={styles.loader}>Loading...</div>
          )}
        </InfiniteScroll>
      </Container>
    </div>
  );
};

const SuspendedAccounts = ({ title }) => {
  const dispatch = useDispatch();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [today, setRemainingDays] = useState(new Date());
  const limit = 20;
  const data = useSelector((state) => state.adoptionMetrics.suspendedAccounts);

  const loading = data?.loading;
  const items = data?.items || [];
  const pagination = data?.pagination || {};
  const { currentPage = 0, totalPages = 0, totalRecordsCount = 0 } = pagination;

  const hasMorePages = items.length < totalRecordsCount;

  useEffect(() => {
    const timer = setInterval(() => setRemainingDays(new Date()), 24 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const getDaysLeft = (item) => {
    const referenceDate = item.changedDate || item.createdDate;
    const suspendedDays = item.noOfDays;

    if (!referenceDate || !suspendedDays) {
      return null;
    }

    const changed = new Date(referenceDate);
    const diffTime = today - changed;
    const remainingDays = Math.ceil(
      (suspendedDays * 24 * 60 * 60 * 1000 - diffTime) / (1000 * 60 * 60 * 24)
    );

    return remainingDays > 0 ? remainingDays : 0;
  };
  const sortedItems = [...items].sort((a, b) => {
    const daysLeftA = getDaysLeft(a);
    const daysLeftB = getDaysLeft(b);

    if (daysLeftA === null && daysLeftB === null) return 0;
    if (daysLeftA === null) return 1;
    if (daysLeftB === null) return -1;
    return daysLeftA - daysLeftB;
  });

  useEffect(() => {
    dispatch(fetchSuspendedAccounts({ page: 0, limit }));
  }, []);

  const handleLoadMore = async () => {
    if (isFetchingMore || loading || items.length >= totalRecordsCount) {
      return;
    }

    setIsFetchingMore(true);
    try {
      await dispatch(fetchSuspendedAccounts({ page: currentPage + 1, limit }));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const showLoader = loading || isFetchingMore;

  return (
    <div className={styles.productMetricsCol}>
      <span className={styles.title}>
        {title} {totalRecordsCount > 0 && <span>({totalRecordsCount})</span>}
      </span>

      <Container>
        <InfiniteScroll
          loading={showLoader}
          currentPage={currentPage}
          totalPages={totalPages}
          pagination={true}
          setPage={handleLoadMore}
          hasMore={hasMorePages}
        >
          {sortedItems.length > 0 ? (
            <List className={styles.accountList}>
              {sortedItems.map((item, index) => {
                const daysLeft = getDaysLeft(item);

                return (
                  <List.Item key={item.id}>
                    {index + 1}.{' '}
                    <Link
                      to={`/paperflite/accounts/${item.id}/management`}
                      className={styles.accountLink}
                    >
                      {item.name}
                    </Link>{' '}
                    <span className={styles.daysLeftPill}>
                      {daysLeft !== null
                        ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                        : 'N/A'}
                    </span>
                    {index === 0 && (
                      <span className={styles.deleteIndicator}>Terminating soon...</span>
                    )}
                  </List.Item>
                );
              })}
            </List>
          ) : (
            !loading && <div className={styles.emptyMessage}>No Data found</div>
          )}
          {showLoader && (
            <div className={styles.loader}>Loading...</div>
          )}
        </InfiniteScroll>
      </Container>
    </div>
  );
};

const TerminatedAccounts = ({ title }) => {
  const dispatch = useDispatch();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [today, setRemainingDays] = useState(new Date());
  const limit = 20;
  const data = useSelector((state) => state.adoptionMetrics.terminatedAccounts);

  const loading = data?.loading;
  const items = data?.items || [];
  const pagination = data?.pagination || {};
  const { currentPage = 0, totalPages = 0, totalRecordsCount = 0 } = pagination;

  const hasMorePages = items.length < totalRecordsCount;

  useEffect(() => {
    const timer = setInterval(() => setRemainingDays(new Date()), 24 * 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const getDaysLeft = (item) => {
    const referenceDate = item.changedDate || item.createdDate;
    const terminatedDays = item.noOfDays;

    if (!referenceDate || !terminatedDays) {
      return null;
    }

    const changed = new Date(referenceDate);
    const diffTime = today - changed;
    const remainingDays = Math.ceil(
      (terminatedDays * 24 * 60 * 60 * 1000 - diffTime) / (1000 * 60 * 60 * 24)
    );

    return remainingDays > 0 ? remainingDays : 0;
  };

  const sortedItems = [...items].sort((a, b) => {
    const daysLeftA = getDaysLeft(a);
    const daysLeftB = getDaysLeft(b);

    if (daysLeftA === null && daysLeftB === null) return 0;
    if (daysLeftA === null) return 1;
    if (daysLeftB === null) return -1;
    return daysLeftA - daysLeftB;
  });

  useEffect(() => {
    dispatch(fetchTerminatedAccounts({ page: 0, limit }));
  }, []);

  const handleLoadMore = async () => {
    if (isFetchingMore || loading || items.length >= totalRecordsCount) {
      return;
    }

    setIsFetchingMore(true);
    try {
      await dispatch(fetchTerminatedAccounts({ page: currentPage + 1, limit }));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsFetchingMore(false);
    }
  };

  const showLoader = loading || isFetchingMore;

  return (
    <div className={styles.productMetricsCol}>
      <span className={styles.title}>
        {title} {totalRecordsCount > 0 && <span>({totalRecordsCount})</span>}
      </span>

      <Container>
        <InfiniteScroll
          loading={showLoader}
          currentPage={currentPage}
          totalPages={totalPages}
          pagination={true}
          setPage={handleLoadMore}
          hasMore={hasMorePages}
        >
          {sortedItems.length > 0 ? (
            <List className={styles.accountList}>
              {sortedItems.map((item, index) => {
                const daysLeft = getDaysLeft(item);

                return (
                  <List.Item key={item.id}>
                    {index + 1}.{' '}
                    <Link
                      to={`/paperflite/accounts/${item.id}/management`}
                      className={styles.accountLink}
                    >
                      {item.name}
                    </Link>{' '}
                    <span className={styles.daysLeftPill}>
                      {daysLeft !== null
                        ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                        : 'N/A'}
                    </span>
                    {index === 0 && (
                      <span className={styles.deleteIndicator}>Deleting soon...</span>
                    )}
                  </List.Item>
                );
              })}
            </List>
          ) : (
            !loading && <div className={styles.emptyMessage}>No Data found</div>
          )}
          {showLoader && (
            <div className={styles.loader}>Loading...</div>
          )}
        </InfiniteScroll>
      </Container>
    </div>
  );
};


const ActiveUsers = ({
  dailyActiveUsers,
  weeklyActiveUsers,
  monthlyActiveUsers,
}) => {
  const dailyData = [];
  const weeklyData = [];
  const monthlyData = [];

  const dailyTimeline = (dailyActiveUsers && dailyActiveUsers.data) || [];
  const weeklyTimeline = (weeklyActiveUsers && weeklyActiveUsers.data) || [];
  const monthlyTimeline = (monthlyActiveUsers && monthlyActiveUsers.data) || [];

  const dailyActiveUsersCount =
    (dailyActiveUsers && dailyActiveUsers.count) || 0;
  const weeklyActiveUsersCount =
    (weeklyActiveUsers && weeklyActiveUsers.count) || 0;
  const monthlyActiveUsersCount =
    (monthlyActiveUsers && monthlyActiveUsers.count) || 0;

  dailyTimeline.forEach((item) => {
    dailyData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  weeklyTimeline.forEach((item) => {
    weeklyData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  monthlyTimeline.forEach((item) => {
    monthlyData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("MMM YY"),
      value: item.value,
    });
  });

  return (
    <div className={styles.usersInformation}>
      <span className={styles.title}>Usage Reports</span>

      <div className={styles.usersInformationWrapper}>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <TimeLineChart
                title="Daily Active Users"
                count={dailyActiveUsersCount}
                timeline={dailyData}
                margin={{ top: 20, right: 30, bottom: 20, left: -20 }}
                tooltip="DAU"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={2}>
            <Grid.Column>
              <TimeLineChart
                title="Weekly Active Users"
                count={weeklyActiveUsersCount}
                timeline={weeklyData}
                margin={{ top: 20, right: 30, bottom: 20, left: -20 }}
                tooltip="WAU"
              />
            </Grid.Column>
            <Grid.Column>
              <TimeLineChart
                title="Monthly Active Users"
                count={monthlyActiveUsersCount}
                timeline={monthlyData}
                margin={{ top: 20, right: 30, bottom: 20, left: -20 }}
                tooltip="MAU"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </div>
  );
};

const MetricsCard = ({ title, count }) => {
  return (
    <div className={styles.metrics}>
      <span className={styles.title}>{title}</span>
      <span className={styles.count}>{count}</span>
    </div>
  );
};

const TimeLineChartCustomToolTip = ({ tooltip, active, payload, label }) => {
  if (active && !_.isEmpty(payload)) {
    var item = payload[0].payload;

    return (
      <div className={styles.tooltipBox}>
        <div className={styles.tooltipText}>{item.text}</div>
        <div className={styles.tooltipSubText}>
          {tooltip}: {item.value}
        </div>
      </div>
    );
  }

  return null;
};

const TimeLineChart = ({ title, count, timeline, tooltip, margin }) => {
  return (
    <>
      <div className={styles.timeLineChart}>
        <div className={styles.timeLineChartTitle}>{title}</div>
        <div className={styles.timeLineChartCount}> {count}</div>
        <ResponsiveContainer width={"100%"} height={300}>
          <LineChart width={500} height={300} data={timeline} margin={margin}>
            <XAxis
              dataKey={"text"}
              axisLine={false}
              tick={true}
              className={styles.xLabel}
            />
            <YAxis axisLine={false} />
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <Line
              type="natural"
              dataKey="value"
              stroke="#E50C58"
              strokeWidth={2.5}
              dot={false}
            />
            <Tooltip
              wrapperStyle={{ outlineColor: "#E50C58" }}
              content={<TimeLineChartCustomToolTip tooltip={tooltip} />}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

const Integrations = ({ integrations }) => {
  var integrationsData = integrations;
  if (integrations.length == 1) {
    integrationsData.push({ text: "", value: "" });
  }

  const CustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;
    return (
      <text x={x} y={y - 40} width={width} height={height} textAnchor="start">
        {value}
      </text>
    );
  };

  return (
    <div className={styles.usersInformation}>
      <span className={styles.title}>Integrations</span>
      {integrationsData && integrationsData.length > 0 && (
        <BarChart
          width={470}
          height={800}
          data={integrationsData}
          layout="vertical"
          margin={{ top: 65, right: 10, left: 0, bottom: 10 }}
        >
          <CartesianGrid horizontal={false} strokeDasharray="3 3" />
          <XAxis
            tickLine={false}
            tickMargin={10}
            dataKey={"value"}
            axisLine={false}
            type="number"
          />

          <Bar
            dataKey={"value"}
            fill="#e51058"
            maxBarSize={10}
            label={{ position: "right" }}
          />
          <Bar dataKey={"text"} label={<CustomizedLabel />} />
        </BarChart>
      )}
    </div>
  );
};

const CreationBehaviour = ({ streams, collections, assets }) => {
  const streamsData = [];
  const collectionsData = [];
  const assetsData = [];
  var streamsCount = 0,
    collectionsCount = 0,
    assetsCount = 0;

  if (streams && streams.length > 0) {
    var latest = streams[streams.length - 1];
    streamsCount = latest.value;
  }

  streams.forEach((item) => {
    streamsData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  if (collections && collections.length > 0) {
    var latest = collections[collections.length - 1];
    collectionsCount = latest.value;
  }

  collections.forEach((item) => {
    collectionsData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  if (assets && assets.length > 0) {
    var latest = assets[assets.length - 1];
    assetsCount = latest.value;
  }

  assets.forEach((item) => {
    assetsData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  return (
    <div className={styles.usersInformation}>
      <span className={styles.title}>Creation Behaviour</span>
      <Grid>
        <Grid.Row columns={3}>
          <Grid.Column>
            <TimeLineChart
              title={"# of Streams"}
              count={streamsCount}
              timeline={streamsData}
              margin={{ top: 20, right: 30, bottom: 20, left: -40 }}
              tooltip="Streams Created"
            />
          </Grid.Column>
          <Grid.Column>
            <TimeLineChart
              title={"# of Collections"}
              count={collectionsCount}
              timeline={collectionsData}
              margin={{ top: 20, right: 30, bottom: 20, left: -25 }}
              tooltip="Collections Created"
            />
          </Grid.Column>
          <Grid.Column>
            <TimeLineChart
              title={"# of Assets"}
              count={assetsCount}
              timeline={assetsData}
              margin={{ top: 20, right: 30, bottom: 20, left: -10 }}
              tooltip="Assets Created"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const AssetBehaviour = ({ assetSharing, assetViewing }) => {
  const assetViewingData = [];
  const assetSharingData = [];
  var assetViewingCount = 0,
    assetSharingCount = 0;

  if (assetViewing && assetViewing.length > 0) {
    var latest = assetViewing[assetViewing.length - 1];
    assetViewingCount = latest.value;
  }

  assetViewing.forEach((item) => {
    assetViewingData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  if (assetSharing && assetSharing.length > 0) {
    var latest = assetSharing[assetSharing.length - 1];
    assetSharingCount = latest.value;
  }

  assetSharing.forEach((item) => {
    assetSharingData.push({
      date: moment(item.date).toDate(),
      text: moment(item.date).format("DD-MMM-YY"),
      value: item.value,
    });
  });

  return (
    <div className={styles.usersInformation}>
      <span className={styles.title}>Asset Behaviour</span>
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <TimeLineChart
              title="# of Asset Viewed"
              count={assetViewingCount}
              timeline={assetViewingData}
              margin={{ top: 20, right: 30, bottom: 20, left: -15 }}
              tooltip="Asset Viewes"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Grid>
        <Grid.Row>
          <Grid.Column>
            <TimeLineChart
              title="# of Asset Shared"
              count={assetSharingCount}
              timeline={assetSharingData}
              margin={{ top: 20, right: 30, bottom: 20, left: -25 }}
              tooltip="Asset Shared"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const FliteviewMetriscs = () => {
  const emailOpentRate = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.emailOpenRate", 0)
  );

  const linkOpenRate = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.linkOpenRate", 0)
  );

  const averageTimeSpent = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.averageTimeSpent", 0)
  );

  const personalisedLinks = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.personalisedLinks", 0)
  );

  const anonymousLinks = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.anonymousLinks", 0)
  );

  const sessions = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.sessions", 0)
  );

  const personalisedSessions = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.personalisedSessions", 0)
  );

  const anonymousSessions = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.anonymousSessions", 0)
  );

  const completionSessions = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.completionSessions", 0)
  );

  const uniqueVisitors = useSelector((state) =>
    _.get(state, "adoptionMetrics.fliteviewMetrics.uniqueVisitors", 0)
  );

  return (
    <div className={styles.fliteview}>
      <div className={styles.title}>Fliteview usage</div>

      <Grid>
        <Grid.Row columns={3}>
          <Grid.Column width={10}>
            <Grid>
              <Grid.Row columns={2}>
                <Grid.Column width={8}>
                  <FliteviewMetricsCard
                    title="% of Email Open Rate"
                    metrics={emailOpentRate.count}
                    isPercent={true}
                    format={true}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FliteviewMetricsCard
                    title="% of Link Click Rate"
                    metrics={linkOpenRate.count}
                    isPercent={true}
                    format={true}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <FliteviewSessionsMetricsCard
                    sessions={sessions.count}
                    personalised={personalisedSessions.count}
                    anonymous={anonymousSessions.count}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
          <Grid.Column width={6}>
            <Grid>
              <Grid.Row>
                <Grid.Column>
                  <FliteviewMetricsCard
                    title="# of Personalized Links"
                    metrics={personalisedLinks.count}
                  />
                  <FliteviewMetricsCard
                    title="# of Anonymous Links"
                    metrics={anonymousLinks.count}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={3}>
          <Grid.Column width={5}>
            <FliteviewMetricsCard
              title="# of Unique Visitors"
              metrics={uniqueVisitors.count}
              isPercent={false}
            />
          </Grid.Column>
          <Grid.Column width={5}>
            <FliteviewMetricsCard
              title="% of Completion"
              metrics={completionSessions.count}
              isPercent={true}
              format={true}
            />
          </Grid.Column>

          <Grid.Column width={6}>
            <FliteviewMetricsCard
              title="Average Time Spent"
              metrics={averageTimeSpent.count}
              isPercent={false}
              format={true}
              isMinutes={true}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const FliteviewSessionsMetricsCard = ({
  sessions,
  personalised,
  anonymous,
}) => {
  return (
    <div className={styles.sessionCard}>
      <Grid>
        <Grid.Row className={styles.cardRow}>
          <Grid.Column>
            <div
              className={styles.cardTitle}
              style={{ float: "left", marginRight: "16px" }}
            >
              # of Views/Sessions{" "}
            </div>
            <div className={styles.metrics}> {sessions} </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className={styles.cardRow}>
          <Grid.Column>
            <div className={styles.sessionTitle}> Personalized </div>
            <div className={styles.sessionMetrics}>{personalised}</div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className={styles.cardRow}>
          <Grid.Column>
            <div className={styles.sessionTitle}> Anonymous </div>
            <div className={styles.sessionMetrics}>{anonymous}</div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const FliteviewMetricsCard = ({
  title,
  metrics,
  isPercent,
  format,
  isMinutes,
}) => {
  return (
    <div className={styles.card}>
      <Grid>
        <Grid.Row className={styles.cardRow}>
          <Grid.Column>
            <div className={styles.cardTitle}>{title}</div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className={styles.cardRow}>
          <Grid.Column>
            <div className={styles.metrics}>
              {metrics && format ? metrics.toFixed(2) : metrics}
              <span>{isPercent ? "%" : ""}</span>
              <span className={styles.minutes}>
                {isMinutes ? " minutes" : ""}
              </span>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const DateRangeSelection = (props) => {
  const dateRangeOptions = [
    { key: 2, text: "One Month", value: "one_month" },
    { key: 3, text: "Three Months", value: "three_months" },
    { key: 4, text: "Six Months", value: "six_months" },
    { key: 5, text: "One Year", value: "one_year" },
  ];

  const getDateRangeText = () => {
    const selected = dateRangeOptions.find(
      (item) => item.value === props.dateRange
    );
    return selected.text;
  };

  return (
    <Dropdown
      selection
      text={getDateRangeText()}
      value={props.dateRange}
      onChange={(e, data) => props.onSetDateRange(data.value)}
      options={dateRangeOptions}
    />
  );
};

export default function Dashboard() {
  const dispatch = useDispatch();

  const totalAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.totalAccounts", {})
  );
  const newAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.newAccounts.items", [])
  );
  const churnedAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.churnedAccounts.items", [])
  );
  const trialAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.trialAccounts", [])
  );
  const suspendedAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.suspendedAccounts.items", [])
  );
  const terminatedAccounts = useSelector((state) =>
    _.get(state, "adoptionMetrics.terminatedAccounts.items", [])
  );
  const totalUsers = useSelector((state) =>
    _.get(state, "adoptionMetrics.accountMetrics.users", 0)
  );
  const totalAssets = useSelector((state) =>
    _.get(state, "adoptionMetrics.accountMetrics.assets", 0)
  );
  const totalCollections = useSelector((state) =>
    _.get(state, "adoptionMetrics.accountMetrics.collections", 0)
  );
  const totalStreams = useSelector((state) =>
    _.get(state, "adoptionMetrics.accountMetrics.streams", 0)
  );

  const totalLeads = useSelector((state) =>
    _.get(state, "adoptionMetrics.accountMetrics.leads", 0)
  );

  const dailyActiveUsers = useSelector((state) =>
    _.get(state, "adoptionMetrics.userMetrics.daily")
  );
  const weeklyActiveUsers = useSelector((state) =>
    _.get(state, "adoptionMetrics.userMetrics.weekly")
  );
  const monthlyActiveUsers = useSelector((state) =>
    _.get(state, "adoptionMetrics.userMetrics.monthly")
  );
  const streamCreation = useSelector((state) =>
    _.get(state, "adoptionMetrics.streamCreation.items", [])
  );
  const collectionCreation = useSelector((state) =>
    _.get(state, "adoptionMetrics.collectionCreation.items", [])
  );
  const assetCreation = useSelector((state) =>
    _.get(state, "adoptionMetrics.assetCreation.items", [])
  );
  const assetViewing = useSelector((state) =>
    _.get(state, "adoptionMetrics.assetViewing.items", [])
  );
  const assetSharing = useSelector((state) =>
    _.get(state, "adoptionMetrics.assetSharing.items", [])
  );
  const integrations = useSelector((state) =>
    _.get(state, "adoptionMetrics.integrations.items", [])
  );

  const accounts =
    useSelector((state) => _.get(state, "adoptionMetrics.accounts.items")) ||
    [];

  const [accountType, setAccountType] = useState(null);
  const [accountOptions, setAccountOptions] = useState([]);

  const [managers, setManagers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [mrrRanges, setMrrRanges] = useState([]);
  const [useCases, setUseCases] = useState([]);

  const [dateRange, setDateRange] = useState("one_month");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [managerOptions, setManagerOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  const adminAppUsers = useSelector((state) => _.get(state, "accounts.adminAppUsers"));

  if (!_.isEmpty(initCountryOptions) && _.isEmpty(countryOptions)) {
    let options = _.cloneDeep(initCountryOptions);
    options.map(item => {
      item.label = item.text;
    });

    setCountryOptions(options);
  }

  if (!_.isEmpty(initIndustryOptions) && _.isEmpty(industryOptions)) {
    
    let options = _.cloneDeep(initIndustryOptions);
    options.map(item => {
      item.label = item.text;
    });

    setIndustryOptions(options);

  }

  if (!_.isEmpty(adminAppUsers) && _.isEmpty(managerOptions)) {
    let options = _.map(adminAppUsers, (user) => {
      return { key: user.id, label: user.name, value: user.id };
    });

    setManagerOptions(options);
  } 

  useEffect(() => {
    const to = moment()
      .subtract(1, "days")
      .format("YYYY-MM-DD");

    const lastTwoWeek = moment()
      .subtract(14, "days")
      .format("YYYY-MM-DD");

    dispatch(fetchNewAccounts({ from: lastTwoWeek, to: to }));
    dispatch(fetchChurnedAccounts({ from: lastTwoWeek, to: to }));

    dispatch(fetchAdoptionAccounts());
    dispatch(fetchTotalAccounts({ subscriptionType: "PAID" }));

    const accountsTimelineFrom = moment()
      .subtract(3, "months")
      .format("YYYY-MM-DD");

    dispatch(
      fetchTotalAccountsTimeline({ from: accountsTimelineFrom, to: to })
    );
  }, []);

  const getFilters = () => {

    let filters = {};
    filters.account = accountType;

    if (!_.isEmpty(managers)) {
      filters.managers = managers.map((item) => item.value);
    }

    if (!_.isEmpty(countries)) {
      filters.countries = countries.map((item) => item.value);
    }

    if (!_.isEmpty(mrrRanges)) {
      filters.mrrRanges = mrrRanges.map((item) => item.value);
    }

    if (!_.isEmpty(industries)) {
      filters.industries = industries.map((item) => item.value);
    }

    if (!_.isEmpty(useCases)) {
      filters.useCases = useCases.map((item) => item.value);
    }

    return filters;
  }

  useEffect(() => {

    let filters = getFilters();

    dispatch(fetchWeeklyActiveUsers(filters));
    dispatch(fetchWeeklyActiveUsersTimeline(filters));
    dispatch(fetchMonthlyActiveUsers(filters));
    dispatch(fetchMonthlyActiveUsersTimeline(filters));

    const to = moment()
      .subtract(1, "days")
      .format("YYYY-MM-DD");

    filters.from = to;
    filters.to = to;

    dispatch(fetchTotalUsers(filters));
    dispatch(fetchTotalAssets(filters));

    dispatch(fetchTotalCollections(filters));
    dispatch(fetchTotalStreams(filters));
    dispatch(fetchTotalLeads(filters));

    console.log("filters: ", filters);

  }, [accountType, managers, countries, industries, useCases, mrrRanges]);

  useEffect(() => { }, []);

  useEffect(() => {
    if (fromDate && toDate) {

      let filters = getFilters();

      // daily active users
      dispatch(fetchDailyActiveUsers(filters));

      filters.from = fromDate;
      filters.to = toDate;

      dispatch(fetchIntegrations(filters));
      dispatch(fetchDailyActiveUsersTimeline(filters));

      // creation behaviour
      dispatch(fetchStreamCreation(filters));
      dispatch(fetchAssetCreation(filters));
      dispatch(fetchCollectionCreation(filters));
      dispatch(fetchAssetViewing(filters));
      dispatch(fetchAssetSharing(filters));

      // fliteview useage
      dispatch(fetchEmailOpenRate(filters));
      dispatch(fetchLinkOpenRate(filters));

      dispatch(fetchAverageTimeSpent(filters));
      dispatch(fetchPersonalisedLinks(filters));
      dispatch(fetchAnonymousLinks(filters));
      dispatch(fetchSessions(filters));

      dispatch(fetchPersonalisedSessions(filters));
      dispatch(fetchAnonymousSessions(filters));
      dispatch(fetchUniqueVisitors(filters));
      dispatch(fetchSessionsCompletion(filters));
    }
  }, [accountType, fromDate, toDate, managers, countries, industries, useCases, mrrRanges]);

  useEffect(() => {
    var from = moment()
      .subtract(14, "days")
      .format("YYYY-MM-DD");

    const to = moment()
      .subtract(1, "days")
      .format("YYYY-MM-DD");

    switch (dateRange) {
      case "one_week":
        from = moment()
          .subtract(7, "days")
          .format("YYYY-MM-DD");
        break;
      case "two_weeks":
        from = moment()
          .subtract(14, "days")
          .format("YYYY-MM-DD");
        break;
      case "one_month":
        from = moment()
          .subtract(1, "months")
          .format("YYYY-MM-DD");
        break;
      case "three_months":
        from = moment()
          .subtract(3, "months")
          .format("YYYY-MM-DD");
        break;
      case "six_months":
        from = moment()
          .subtract(6, "months")
          .format("YYYY-MM-DD");
        break;
      case "one_year":
        from = moment()
          .subtract(1, "years")
          .format("YYYY-MM-DD");
        break;
      default:
        from = null;
        to = null;
        break;
    }

    setFromDate(from);
    setToDate(to);
  }, [dateRange]);

  useEffect(() => {
    const options = [];
    var allAccounts = [];
    allAccounts = [{ name: "All Accounts", value: null }, ...accounts];
    allAccounts.forEach((item, index) => {
      options.push({ key: index, text: item.name, value: item.id });
    });
    setAccountOptions(options);
  }, [accounts]);

  const onSetDateRange = (dateRange) => {
    setDateRange(dateRange);
  };

  return (
    <Container className={styles.container}>
      <Grid>
        <Grid.Row className={styles.productMetricsRow} columns={3}>
          <Grid.Column>
            <Accounts totalAccounts={totalAccounts} />
          </Grid.Column>
          <Grid.Column>
            <AccountsList title={"New Accounts"} data={newAccounts} />
          </Grid.Column>
          <Grid.Column>
            <AccountsList title={"Churned Accounts"} data={churnedAccounts} />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className={styles.productMetricsRow} columns={3}>
          <Grid.Column>
            {<TrialAccounts title={"Trial Accounts"} data={trialAccounts} />}
          </Grid.Column>
          <Grid.Column>
            <SuspendedAccounts title={"Suspended Accounts"} data={suspendedAccounts} />
          </Grid.Column>
          <Grid.Column>
            <TerminatedAccounts title={"Terminated Accounts"} data={terminatedAccounts} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className={styles.productMetricsRow}>
          <Grid.Column>
            <WeeklyAdoption />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className={styles.productMetricsRow} columns={3}>
          <Grid.Column>
            <Form.Dropdown
              className={styles.accountOptions}
              id="accounts"
              label=""
              placeholder="All Accounts"
              value={accountType}
              onChange={(e, data) => { 
                setAccountType(data.value);
                setCountries([]);
                setMrrRanges([]);
                setIndustries([]);
                setUseCases([]);
                setManagers([]);

              }}
              options={accountOptions}
              selection
              search
            />
          </Grid.Column>
          <Grid.Column className={cx(styles.countryDropDown)}>
            <DateRangeSelection
              dateRange={dateRange}
              onSetDateRange={onSetDateRange}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row columns={5} className={styles.accountMetricsRow}>
          <Grid.Column>

            <Select
              isMulti
              value={managers}
              name="managers"
              options={managerOptions}
              onChange={selected => {
                setAccountType(null);
                setManagers(selected);
              }}
              placeholder="Choose a CSM"
              className={cx(styles.selectFilter)}
              classNamePrefix="select"
              styles={{ color: "red" }}
            />

          </Grid.Column>
          <Grid.Column>
            <Select
              isMulti
              value={countries}
              options={countryOptions}
              onChange={selecetd => { 
                setAccountType(null);
                setCountries(selecetd);
              }}
              placeholder="Choose a country"
              className={cx("basic-multi-select")}
              classNamePrefix="select"

            />
          </Grid.Column>
          <Grid.Column>
            <Select
              isMulti
              value={mrrRanges}
              options={intMrrOptions}
              onChange={selecetd => {
                setAccountType(null);
                setMrrRanges(selecetd);
              }}
              placeholder="Choose the MRR (Range)"
              className={cx("basic-multi-select")}
              classNamePrefix="select"

            />
          </Grid.Column>
          <Grid.Column>
            <Select
              isMulti
              value={industries}
              options={industryOptions}
              onChange={selecetd => { 
                setAccountType(null);
                setIndustries(selecetd);
              }}
              placeholder="Choose the industry"
              className={cx("basic-multi-select")}
              classNamePrefix="select"
            />
          </Grid.Column>
          <Grid.Column>
            <Select
              isMulti
              value={useCases}
              options={initUseCaseOptions}
              onChange={selecetd => { 
                setAccountType(null);
                setUseCases(selecetd);
              }}
              placeholder="Choose the usecase"
              className={cx("basic-multi-select")}
              classNamePrefix="select"
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row columns={5} className={styles.accountMetricsRow}>
          <Grid.Column>
            <MetricsCard title={"# of Users"} count={totalUsers} />
          </Grid.Column>
          <Grid.Column>
            <MetricsCard title={"# of Assets"} count={totalAssets} />
          </Grid.Column>
          <Grid.Column>
            <MetricsCard title={"# of Collections"} count={totalCollections} />
          </Grid.Column>
          <Grid.Column>
            <MetricsCard title={"# of Streams"} count={totalStreams} />
          </Grid.Column>
          <Grid.Column>
            <MetricsCard title={"# of Leads"} count={totalLeads} />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className={styles.userMetricsRow}>
          <Grid.Column className={styles.userMetricsCol}>
            <ActiveUsers
              dailyActiveUsers={dailyActiveUsers}
              weeklyActiveUsers={weeklyActiveUsers}
              monthlyActiveUsers={monthlyActiveUsers}
            />
          </Grid.Column>
          <Grid.Column className={styles.integrationAccountCol}>
            <Integrations integrations={integrations} />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className={styles.userMetricsRow}>
          <Grid.Column>
            <CreationBehaviour
              streams={streamCreation}
              collections={collectionCreation}
              assets={assetCreation}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row className={styles.userMetricsRow} columns={3}>
          <Grid.Column className={styles.assetMetricsCol} width={5}>
            <AssetBehaviour
              assetSharing={assetSharing}
              assetViewing={assetViewing}
            />
          </Grid.Column>
          <Grid.Column width={11}>
            <FliteviewMetriscs />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Container>
  );
}